import { Controller } from "@hotwired/stimulus"

const CAL_BOOKING_URL = "https://cal.com/lumenfall/lumenfall-introduction"

/**
 * Controller for the booking modal with Cal.com appointment scheduling.
 *
 * Opens a modal with an iframe to the Cal.com booking page.
 * Since the booking page doesn't provide postMessage callbacks for booking completion,
 * users close the modal manually after booking.
 *
 * Usage:
 * - Attach to a <dialog> element with the iframe inside
 * - Trigger via window event: window.dispatchEvent(new CustomEvent('show-booking-modal'))
 * - Or via data-action: data-action="click->booking-modal#open"
 */
export default class extends Controller {
  static targets = ["iframe", "loader"]
  static values = {
    url: String,
  }

  declare iframeTarget: HTMLIFrameElement
  declare loaderTarget: HTMLElement
  declare hasLoaderTarget: boolean
  declare urlValue: string

  private boundShowHandler = this.open.bind(this)
  private iframeLoaded = false

  connect() {
    // Force canonical booking URL to avoid stale cached DOM values.
    this.urlValue = CAL_BOOKING_URL

    // Listen for window-level event to show modal
    window.addEventListener("show-booking-modal", this.boundShowHandler as EventListener)
  }

  disconnect() {
    window.removeEventListener("show-booking-modal", this.boundShowHandler as EventListener)
  }

  open(event?: Event) {
    event?.preventDefault()

    const dialog = this.element as HTMLDialogElement
    const expectedUrl = this.urlValue || CAL_BOOKING_URL

    // Show loader on first open
    if (!this.iframeLoaded && this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
    }

    // Load iframe src lazily and fix stale URLs from old cached markup.
    if (!this.iframeTarget.src || this.iframeTarget.src !== expectedUrl) {
      this.iframeTarget.src = expectedUrl
    }

    dialog.showModal()
  }

  onIframeLoad() {
    this.iframeLoaded = true
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden")
    }
  }

  close() {
    const dialog = this.element as HTMLDialogElement
    dialog.close()
  }
}
